import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Check,
  AlertCircle,
  Info,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useAdminStore } from '../../store/admin';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface PaymentProvider {
  id: number;
  provider: 'stripe' | 'paypal';
  mode: 'test' | 'live';
  is_active: boolean;
  test_publishable_key: string;
  test_secret_key: string;
  live_publishable_key: string;
  live_secret_key: string;
}

export default function PaymentSettings() {
  const { adminToken } = useAdminStore();
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state for each provider
  const [formData, setFormData] = useState<Record<string, Partial<PaymentProvider>>>({});

  const fetchPaymentSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/payments/settings`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      if (response.ok) {
        const data = await response.json() as { success: boolean; data: PaymentProvider[] };
        if (data.success && data.data) {
          setProviders(data.data);
          // Initialize form data
          const initialFormData: Record<string, Partial<PaymentProvider>> = {};
          data.data.forEach((p) => {
            initialFormData[p.provider] = {
              mode: p.mode,
              is_active: p.is_active,
              test_publishable_key: p.test_publishable_key || '',
              test_secret_key: p.test_secret_key || '',
              live_publishable_key: p.live_publishable_key || '',
              live_secret_key: p.live_secret_key || '',
            };
          });
          setFormData(initialFormData);
        }
      }
    } catch (error) {
      console.error('Failed to fetch payment settings:', error);
      setError('Failed to load payment settings');
    } finally {
      setIsLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    fetchPaymentSettings();
  }, [fetchPaymentSettings]);

  const handleInputChange = (provider: string, field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value,
      },
    }));
  };

  const handleSave = async (provider: 'stripe' | 'paypal') => {
    setIsSaving(provider);
    setError(null);

    const data = formData[provider];

    // Validate that credentials exist for the selected mode when provider is active
    if (data?.is_active) {
      const mode = data.mode || 'test';
      if (mode === 'live') {
        if (!data.live_publishable_key?.trim() || !data.live_secret_key?.trim()) {
          setError(`Cannot activate ${provider.charAt(0).toUpperCase() + provider.slice(1)} in Live mode without Live credentials. Please enter your Live Client ID and Secret.`);
          setIsSaving(null);
          return;
        }
      } else {
        if (!data.test_publishable_key?.trim() || !data.test_secret_key?.trim()) {
          setError(`Cannot activate ${provider.charAt(0).toUpperCase() + provider.slice(1)} in Test mode without Test credentials. Please enter your Test/Sandbox Client ID and Secret.`);
          setIsSaving(null);
          return;
        }
      }
    }

    try {
      const response = await fetch(`${API_URL}/admin/payments/settings/${provider}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json() as { success: boolean; error?: string };

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save settings');
      }

      setSuccessMessage(`${provider.charAt(0).toUpperCase() + provider.slice(1)} settings saved successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchPaymentSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(null);
    }
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const renderProviderCard = (providerType: 'stripe' | 'paypal') => {
    const provider = providers.find((p) => p.provider === providerType);
    const data = formData[providerType] || {};
    const isStripe = providerType === 'stripe';
    const brandColor = isStripe ? '#635BFF' : '#003087';
    const currentMode = data.mode || 'test';

    // Check if credentials are configured for the current mode
    const hasCredentialsForMode = currentMode === 'live'
      ? !!(data.live_publishable_key?.trim() && data.live_secret_key?.trim())
      : !!(data.test_publishable_key?.trim() && data.test_secret_key?.trim());

    const credentialWarning = data.is_active && !hasCredentialsForMode;

    return (
      <motion.div variants={fadeInUp} className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
        {/* Credential Warning */}
        {credentialWarning && (
          <div className="mb-4 flex items-start sm:items-center space-x-2 bg-orange-50 border border-orange-200 text-orange-600 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
            <span className="text-xs sm:text-sm">
              {currentMode === 'live'
                ? 'Live credentials are missing. Payments will fail until Live Client ID and Secret are configured.'
                : 'Test credentials are missing. Payments will fail until Test Client ID and Secret are configured.'}
            </span>
          </div>
        )}

        {/* Provider Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-200">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${brandColor}15` }}
            >
              {isStripe ? (
                <CreditCard className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: brandColor }} />
              ) : (
                <span className="font-bold text-base sm:text-lg" style={{ color: brandColor }}>PP</span>
              )}
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-slate-900">
                {isStripe ? 'Stripe' : 'PayPal'}
              </h3>
              <p className="text-xs sm:text-sm text-slate-500">
                {isStripe ? 'Accept credit card payments' : 'Accept PayPal payments'}
              </p>
            </div>
          </div>

          {/* Active Toggle */}
          <button
            onClick={() => handleInputChange(providerType, 'is_active', !data.is_active)}
            className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-xl transition-colors w-full sm:w-auto ${
              data.is_active
                ? 'bg-green-100 text-green-600'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            {data.is_active ? (
              <>
                <ToggleRight className="w-5 h-5" />
                <span className="font-medium">Active</span>
              </>
            ) : (
              <>
                <ToggleLeft className="w-5 h-5" />
                <span className="font-medium">Inactive</span>
              </>
            )}
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-600 mb-3">
            Environment Mode
          </label>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={() => handleInputChange(providerType, 'mode', 'test')}
              className={`flex-1 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all ${
                currentMode === 'test'
                  ? 'bg-orange-100 text-orange-600 border-2 border-orange-400'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border-2 border-transparent'
              }`}
            >
              Test Mode
            </button>
            <button
              onClick={() => handleInputChange(providerType, 'mode', 'live')}
              className={`flex-1 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all ${
                currentMode === 'live'
                  ? 'bg-green-100 text-green-600 border-2 border-green-400'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200 border-2 border-transparent'
              }`}
            >
              Live Mode
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-2 flex items-center space-x-1">
            <Info className="w-3 h-3 flex-shrink-0" />
            <span>
              {currentMode === 'test'
                ? 'Test mode uses sandbox credentials for testing'
                : 'Live mode processes real payments'}
            </span>
          </p>
        </div>

        {/* Test Keys */}
        <div className="space-y-4 mb-6">
          <h4 className="font-medium text-slate-900 flex items-center space-x-2">
            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
            <span>Test Credentials</span>
          </h4>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm text-slate-500 mb-1">
                {isStripe ? 'Publishable Key' : 'Client ID'} (Test)
              </label>
              <input
                type="text"
                value={data.test_publishable_key || ''}
                onChange={(e) => handleInputChange(providerType, 'test_publishable_key', e.target.value)}
                placeholder={isStripe ? 'pk_test_...' : 'ATest...'}
                className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 mb-1">
                {isStripe ? 'Secret Key' : 'Client Secret'} (Test)
              </label>
              <div className="relative">
                <input
                  type={showSecrets[`${providerType}_test_secret`] ? 'text' : 'password'}
                  value={data.test_secret_key || ''}
                  onChange={(e) => handleInputChange(providerType, 'test_secret_key', e.target.value)}
                  placeholder={isStripe ? 'sk_test_...' : 'ETest...'}
                  className="w-full h-11 px-4 pr-12 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret(`${providerType}_test_secret`)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showSecrets[`${providerType}_test_secret`] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Live Keys */}
        <div className="space-y-4 mb-6">
          <h4 className="font-medium text-slate-900 flex items-center space-x-2">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span>Live Credentials</span>
          </h4>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm text-slate-500 mb-1">
                {isStripe ? 'Publishable Key' : 'Client ID'} (Live)
              </label>
              <input
                type="text"
                value={data.live_publishable_key || ''}
                onChange={(e) => handleInputChange(providerType, 'live_publishable_key', e.target.value)}
                placeholder={isStripe ? 'pk_live_...' : 'ALive...'}
                className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-500 mb-1">
                {isStripe ? 'Secret Key' : 'Client Secret'} (Live)
              </label>
              <div className="relative">
                <input
                  type={showSecrets[`${providerType}_live_secret`] ? 'text' : 'password'}
                  value={data.live_secret_key || ''}
                  onChange={(e) => handleInputChange(providerType, 'live_secret_key', e.target.value)}
                  placeholder={isStripe ? 'sk_live_...' : 'ELive...'}
                  className="w-full h-11 px-4 pr-12 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret(`${providerType}_live_secret`)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showSecrets[`${providerType}_live_secret`] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={() => handleSave(providerType)}
          disabled={isSaving === providerType}
          className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow disabled:opacity-50"
        >
          {isSaving === providerType ? (
            <span className="flex items-center justify-center space-x-2">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Saving...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <Save className="w-5 h-5" />
              <span>Save {isStripe ? 'Stripe' : 'PayPal'} Settings</span>
            </span>
          )}
        </button>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Payment System</h1>
        <p className="text-sm sm:text-base text-slate-500">
          Configure your payment gateways for processing transactions
        </p>
      </motion.div>

      {/* Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-2 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl"
          >
            <Check className="w-5 h-5 flex-shrink-0" />
            <span>{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Banner */}
      <motion.div variants={fadeInUp} className="bg-purple-50 border border-purple-200 rounded-xl p-3 sm:p-4">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-slate-900 font-medium">How it works</p>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Configure both Test and Live credentials for each payment provider. Select the mode (Test/Live)
              to determine which credentials are used for processing payments. Toggle the provider to Active
              to enable it for your platform.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Payment Providers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {renderProviderCard('stripe')}
        {renderProviderCard('paypal')}
      </div>
    </motion.div>
  );
}
